/**
 * Parsing — Fil d'activité : les entités créées/capturées par DR.
 *
 * Le filtre n'est PAS le calque : une entité DR = une entité qui porte un `DR_META`
 * (estampé à la capture au dessin). On parcourt l'espace objet UNE fois, on gate sur
 * la présence de DR_META (les entités non-DR sont ignorées sans autre calcul), et on
 * ordonne par `createdAt`. Read-only (le highlight ne modifie pas le dessin).
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import type { DrawingModuleType } from "../DrawingSingleton";
import { openAs, getEntityHandle } from "../core/oda";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { isPhotoMarkerEntity } from "../photomarker/detection";
import { isDynamicBlockRef } from "../blocks/dynamic";
import { getPhotoMarkerAttributeValue } from "../photomarker/transform";
import { getModelSpaceId } from "./viewports";
import { readEntityMeta } from "../meta/xdata";
import { getEntityType, EntityType } from "../entities/detection";
import { UNDO_LAYER_NAME } from "../database/layers";
import { getColorInfo, colorInfoToCss } from "../colors/color";

export type ActivityKind =
  | "photoMarker"
  | "text"
  | "leader"
  | "block"
  | "dynamicBlock"
  | "geometry";

export interface ActivityEntity {
  handle: string;
  /** Identité stable (DR_META), clé de corrélation avec l'oplog. */
  entityUid: string;
  layer: string;
  kind: ActivityKind;
  /** Nature CAD réelle (plus fine que `kind`) — sert au choix d'icône. */
  entityType: EntityType;
  /** Contenu texte (OdDbText / OdDbMText / MLeader). */
  text?: string;
  /** Epoch ms de création (DR_META). Absent si non estampé. */
  createdAt?: number;
  /** Libellé affiché sur le marqueur (texte de l'attribut) : numéro ou date/heure selon la légende. */
  markerLabel?: string;
  /** Nom du note_config (désordre) au dessin — libellé self-suffisant. */
  defectName?: string;
  /** Couleur CSS lue SUR l'entité (DWG) — légende-indépendante. */
  color?: string;
  /** Identité stampée à la volée sur du fond de plan (pas une création DR). */
  background: boolean;
}

/** Retire les codes de formatage MText (\fArial…;, \H2x;, \A1;, \P, accolades). */
function stripMTextCodes(raw: string): string {
  return raw
    .replace(/\\P/g, " ")
    .replace(/\\[A-Za-z][^;\\]*;/g, "")
    .replace(/[{}]/g, "")
    .trim();
}

/** Nature de l'élément (via detection.ts) + son contenu texte éventuel. */
export function readKindAndText(
  module: DrawingModuleType,
  id: DrawingModule.OdDbObjectId,
  ent: DrawingModule.OdDbEntity,
): { kind: ActivityKind; entityType: EntityType; text?: string } {
  const type = getEntityType(module, id);

  // Marqueur photo d'abord, quel que soit le type CAD : posé en mode « entité »
  // (et notamment le style « texte seul », qui n'a aucune géométrie), un
  // marqueur est un simple texte porteur du XData DR_PHOTO_MARKER. Tester la
  // signature seulement dans la branche BlockReference le rendait invisible
  // pour l'outil des observations.
  if (isPhotoMarkerEntity(module, id)) {
    return { kind: "photoMarker", entityType: type };
  }

  if (type === EntityType.Text) {
    const asText = module.OdDbText.cast(ent);
    if (!asText) return { kind: "text", entityType: type };
    const s = asText.textString();
    const text = s.toString();
    deleteObj(s);
    deleteObj(asText);
    return { kind: "text", entityType: type, text };
  } else if (type === EntityType.MText) {
    const asMText = module.OdDbMText.cast(ent);
    if (!asMText) return { kind: "text", entityType: type };
    const s = asMText.contents();
    const text = stripMTextCodes(s.toString());
    deleteObj(s);
    deleteObj(asMText);
    return { kind: "text", entityType: type, text };
  } else if (type === EntityType.MLeader) {
    const asLeader = module.OdDbMLeader.cast(ent);
    let text: string | undefined;
    if (asLeader) {
      const mt = asLeader.mtext();
      if (mt) {
        const cs = mt.contents();
        text = stripMTextCodes(cs.toString());
        deleteObj(cs);
        deleteObj(mt);
      }
      deleteObj(asLeader);
    }
    return { kind: "leader", entityType: type, text };
  } else if (type === EntityType.BlockReference) {
    // Dynamic blocks get their own kind so the feed can name and badge them —
    // "Tracé" says nothing about an inserted block.
    return {
      kind: isDynamicBlockRef(module, id) ? "dynamicBlock" : "block",
      entityType: type,
    };
  }

  return { kind: "geometry", entityType: type };
}

/**
 * Libellé du marqueur : texte du 1er attribut du bloc (numéro ou date/heure,
 * selon la légende active au moment de la pose — cf. PhotoMarkerManager.formatLabel).
 */
function readMarkerLabel(
  module: DrawingModuleType,
  id: DrawingModule.OdDbObjectId,
): string | undefined {
  return getPhotoMarkerAttributeValue(module, id) ?? undefined;
}

/** Couleur CSS lue sur l'entité : TrueColor → RGB, ByLayer → couleur du calque, ByACI → palette. */
function entityColorCss(
  module: DrawingModuleType,
  ent: DrawingModule.OdDbEntity,
  backgroundColor: number,
): string | undefined {
  const colObj = ent.color();
  const info = getColorInfo(colObj);
  deleteObj(colObj);
  if (info.method !== "ByLayer") return colorInfoToCss(info, backgroundColor);

  const layerId = ent.layerId();
  if (layerId.isNull()) return undefined;
  const layerRec = openAs(
    layerId,
    module.OdDbLayerTableRecord,
    OpenMode.kForRead,
  );
  const lColObj = layerRec.color();
  const css = colorInfoToCss(getColorInfo(lColObj), backgroundColor);
  deleteObj(lColObj);
  deleteObj(layerRec);
  return css;
}

/**
 * Liste les entités DR (celles qui portent un DR_META), ordonnées par `createdAt`.
 * UNE seule traversée de l'espace objet ; gate meta en premier (perf sur gros dessin).
 */
export function listActivityEntities(
  viewer: DRDrawingViewer,
): ActivityEntity[] {
  const module = viewer.module;
  const backgroundColor = viewer.getEffectiveBackgroundColor();
  const modelSpaceId = getModelSpaceId(viewer);
  if (!modelSpaceId) return [];

  const out: ActivityEntity[] = [];
  const rec = openAs(
    modelSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForRead,
  );
  const iter = rec.newIterator(true, true, false);
  iter.start(true, true);
  while (!iter.done()) {
    const id = iter.objectId();
    // Tout le travail par entité (y compris la lecture meta) DOIT être dans le
    // try : une entité illisible ne doit jamais tuer le scan complet.
    try {
      // Filtre = présence de DR_META (null si absent → entité non-DR ignorée).
      const meta = readEntityMeta(module, id);
      if (meta) {
        const ent = iter.entity(OpenMode.kForRead, false);
        try {
          const layerStr = ent.layer();
          const layer = layerStr.toString();
          deleteObj(layerStr);

          // Entité DR supprimée = masquée sur UNDO_LAYER, garde son meta → exclue.
          if (layer !== UNDO_LAYER_NAME) {
            const { kind, entityType, text } = readKindAndText(module, id, ent);
            out.push({
              handle: getEntityHandle(id),
              entityUid: meta.entityUid,
              layer,
              kind,
              entityType,
              text,
              createdAt: meta.createdAt ?? undefined,
              defectName: meta.defectName ?? undefined,
              markerLabel:
                kind === "photoMarker"
                  ? readMarkerLabel(module, id)
                  : undefined,
              color: entityColorCss(module, ent, backgroundColor),
              background: meta.background,
            });
          }
        } finally {
          deleteObj(ent);
        }
      }
    } catch {
      // entité illisible — on ignore
    }
    iter.step(true, true);
  }
  deleteObj(iter);
  deleteObj(rec);

  // Tri chronologique : createdAt réel, fallback handle (~ordre de création) pour
  // un meta sans createdAt (ex. entité de spike S1).
  const orderKey = (e: ActivityEntity): number =>
    e.createdAt ?? parseInt(e.handle, 16);
  out.sort((a, b) => orderKey(a) - orderKey(b));
  return out;
}
